import { create } from "@bufbuild/protobuf";

import { SourceType } from "@gx/ts-contracts/services/database/models/public_pb";
import {
  CreateSourceRequest,
  CreateSourceRequestSchema,
  CreateSourceResponse,
} from "@gx/ts-contracts/services/source/api/source_pb";
import {
  FileUploadIntentSchema,
  UploadMode,
} from "@gx/ts-contracts/services/source/resources/file_upload_intent/file_upload_intent_pb";
import { SourceConfigurationSchema } from "@gx/ts-contracts/services/source/resources/source_configuration/source_configuration_pb";
import {
  SourceTextConfigurationSchema,
  SourceTextFileSchema,
} from "@gx/ts-contracts/services/source/resources/source_configuration/text/text_pb";
import type { UploadSyncResource } from "@gx/ts-contracts/services/uploader/resources/uploader_pb";

import {
  CREATE_SOURCE_TEXT_CONTENT_MAX_LENGTH_ERROR,
  CREATE_SOURCE_TEXT_CONTENT_REQUIRED_ERROR,
  SOURCE_TEXT_CONFIGURATION_CASE,
  SOURCE_TEXT_MAX_CHARACTERS_LENGTH,
} from "@/pages/sources/components/create/sources/text/constants";
import { CreateSourceTextState } from "@/pages/sources/components/create/sources/text/types";
import { isCreateSourceValueProvided } from "@/pages/sources/components/create/sources/utils";

export interface TextSourceProvisioningResult {
  sourceId: string;
  syncId: string;
  uploadSync: UploadSyncResource;
}

export function textToFile(textContent: string, fileName: string): File {
  const blob = new Blob([textContent], { type: "text/plain" });
  return new File([blob], fileName, { type: "text/plain" });
}

function buildSourceTextFile(file: File) {
  return create(SourceTextFileSchema, {
    intent: create(FileUploadIntentSchema, {
      sourcePath: file.name,
      uploadMode: UploadMode.SOURCE,
      fileSize: BigInt(file.size),
    }),
  });
}

export function buildCreateSourceTextRequest(
  sourceName: string,
  textContent: string,
  fileName: string,
): CreateSourceRequest {
  const file = textToFile(textContent, fileName);

  return create(CreateSourceRequestSchema, {
    name: sourceName.trim(),
    type: SourceType.TEXT,
    configuration: create(SourceConfigurationSchema, {
      configuration: {
        case: SOURCE_TEXT_CONFIGURATION_CASE,
        value: create(SourceTextConfigurationSchema, {
          files: [buildSourceTextFile(file)],
        }),
      },
    }),
  });
}

export function extractProvisioningResult(
  response: CreateSourceResponse,
): TextSourceProvisioningResult {
  const sourceId = response.source?.id;
  if (!sourceId) {
    throw new Error("Create source response did not include a source id");
  }

  const sync = response.sync;
  if (!sync || !sync.id) {
    throw new Error("Create source response did not include a sync resource");
  }

  if (sync.syncData.case !== "uploadSync" || !sync.syncData.value) {
    throw new Error("Create source response did not include upload sync data");
  }

  const uploadSync = sync.syncData.value;
  if (!uploadSync.id) {
    throw new Error("Create source response did not include an upload sync");
  }

  if (uploadSync.operations.length === 0) {
    throw new Error("Create source response did not include upload operations");
  }

  return {
    sourceId,
    syncId: sync.id,
    uploadSync,
  };
}

export function getCreateSourceTextContentError(state: CreateSourceTextState): string | null {
  if (state.shouldShowErrors && !isCreateSourceValueProvided(state.textContent)) {
    return CREATE_SOURCE_TEXT_CONTENT_REQUIRED_ERROR;
  }
  if (state.textContent.length > SOURCE_TEXT_MAX_CHARACTERS_LENGTH) {
    return CREATE_SOURCE_TEXT_CONTENT_MAX_LENGTH_ERROR;
  }
  return null;
}
